import { useState, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import ParticleSphere from "./ParticleSphere";
import "./App.css";

function App() {
  const [rotationDirection, setRotationDirection] = useState<number>(1);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [particleCount, setParticleCount] = useState<number>(8000);
  const [rotationSpeed, setRotationSpeed] = useState<number>(0.05);
  const [waveAmplitude, setWaveAmplitude] = useState<number>(0.2);
  const [particleSize, setParticleSize] = useState<number>(0.005);
  const [clustering, setClustering] = useState<number>(3);
  const [particleColor, setParticleColor] = useState<string>("#ffffff");
  const [backgroundColor, setBackgroundColor] = useState<string>("#000000");
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [transparentBackground, setTransparentBackground] =
    useState<boolean>(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  const toggleDirection = () => {
    setRotationDirection((prev) => prev * -1);
  };

  const startRecording = () => {
    const canvas = document.querySelector("canvas");
    if (!canvas) return;

    const stream = canvas.captureStream(60); // 60 FPS

    // VP9 codec supports alpha channel (transparency)
    // VP8 also supports it but VP9 has better compression
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm;codecs=vp9",
      videoBitsPerSecond: 8000000,
    });

    chunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = transparentBackground ? "-transparent" : "";
      a.download = `particle-sphere${suffix}-${Date.now()}.webm`;
      a.click();
      URL.revokeObjectURL(url);
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecording(true);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  return (
    <div className="App">
      {/* Settings Sidebar */}
      <div
        style={{
          position: "fixed",
          left: 0,
          top: 0,
          width: "300px",
          height: "100vh",
          backgroundColor: "rgba(20, 20, 20, 0.95)",
          color: "#ffffff",
          padding: "20px",
          overflowY: "auto",
          zIndex: 1000,
          boxShadow: "2px 0 10px rgba(0,0,0,0.5)",
          fontFamily: "'Courier New', monospace",
        }}
      >
        <h2
          style={{
            marginTop: 0,
            marginBottom: "20px",
            fontWeight: "normal",
            fontSize: "18px",
          }}
        >
          SETTINGS
        </h2>

        {/* Pause/Play */}
        <div style={{ marginBottom: "15px" }}>
          <button
            onClick={() => setIsPaused(!isPaused)}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              cursor: "pointer",
              backgroundColor: "transparent",
              color: "white",
              border: "1px solid #ffffff",
              borderRadius: "3px",
              fontFamily: "'Courier New', monospace",
            }}
          >
            {isPaused ? "▶ PLAY" : "⏸ PAUSE"}
          </button>
        </div>

        {/* Rotation Direction */}
        <div style={{ marginBottom: "15px" }}>
          <button
            onClick={toggleDirection}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              cursor: "pointer",
              backgroundColor: "transparent",
              color: "white",
              border: "1px solid #ffffff",
              borderRadius: "3px",
              fontFamily: "'Courier New', monospace",
            }}
          >
            {rotationDirection === 1 ? "↺ CLOCKWISE" : "↻ COUNTER-CW"}
          </button>
        </div>

        {/* Recording */}
        <div style={{ marginBottom: "16px" }}>
          <button
            onClick={isRecording ? stopRecording : startRecording}
            style={{
              width: "100%",
              padding: "10px",
              fontSize: "14px",
              cursor: "pointer",
              backgroundColor: "transparent",
              color: isRecording ? "#ff4444" : "white",
              border: `1px solid ${isRecording ? "#ff4444" : "#ffffff"}`,
              borderRadius: "3px",
              fontFamily: "'Courier New', monospace",
            }}
          >
            {isRecording ? "⏹ STOP RECORDING" : "⏺ RECORD VIDEO"}
          </button>
        </div>

        {/* Transparent Background Toggle */}
        <div style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            <input
              type="checkbox"
              checked={transparentBackground}
              onChange={(e) => setTransparentBackground(e.target.checked)}
              style={{
                marginRight: "10px",
                cursor: "pointer",
                width: "16px",
                height: "16px",
              }}
            />
            TRANSPARENT BACKGROUND
          </label>
        </div>

        <hr style={{ borderColor: "#444", margin: "20px 0" }} />

        {/* Particle Count */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            PARTICLE COUNT: {particleCount}
          </label>
          <input
            type="range"
            min="1000"
            max="15000"
            step="500"
            value={particleCount}
            onChange={(e) => setParticleCount(Number(e.target.value))}
          />
        </div>

        {/* Rotation Speed */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            ROTATION SPEED: {rotationSpeed.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="0.5"
            step="0.01"
            value={rotationSpeed}
            onChange={(e) => setRotationSpeed(Number(e.target.value))}
          />
        </div>

        {/* Wave Amplitude */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            WAVE AMPLITUDE: {waveAmplitude.toFixed(2)}
          </label>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={waveAmplitude}
            onChange={(e) => setWaveAmplitude(Number(e.target.value))}
          />
        </div>

        {/* Particle Size */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            PARTICLE SIZE: {particleSize.toFixed(3)}
          </label>
          <input
            type="range"
            min="0.001"
            max="0.02"
            step="0.001"
            value={particleSize}
            onChange={(e) => setParticleSize(Number(e.target.value))}
          />
        </div>

        {/* Clustering */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            POLE CLUSTERING: {clustering.toFixed(1)}
          </label>
          <input
            type="range"
            min="0.5"
            max="5"
            step="0.1"
            value={clustering}
            onChange={(e) => setClustering(Number(e.target.value))}
          />
          <small
            style={{
              color: "#888",
              fontSize: "10px",
              display: "block",
              marginTop: "5px",
            }}
          >
            HIGHER = MORE PARTICLES AT POLES
          </small>
        </div>

        <hr style={{ borderColor: "#444", margin: "20px 0" }} />

        {/* Particle Color */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            PARTICLE COLOR
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="color"
              value={particleColor}
              onChange={(e) => setParticleColor(e.target.value)}
              style={{
                width: "50px",
                height: "30px",
                border: "1px solid #ffffff",
                cursor: "pointer",
                backgroundColor: "transparent",
              }}
            />
            <span style={{ fontSize: "12px", color: "#ccc" }}>
              {particleColor}
            </span>
          </div>
        </div>

        {/* Background Color */}
        <div style={{ marginBottom: "18px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "12px",
              color: "#ccc",
            }}
          >
            BACKGROUND COLOR
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <input
              type="color"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              style={{
                width: "50px",
                height: "30px",
                border: "1px solid #ffffff",
                cursor: "pointer",
                backgroundColor: "transparent",
              }}
            />
            <span style={{ fontSize: "12px", color: "#ccc" }}>
              {backgroundColor}
            </span>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
        style={{
          background: transparentBackground ? "transparent" : backgroundColor,
          marginLeft: "300px",
        }}
        gl={{
          alpha: true,
          premultipliedAlpha: false,
          preserveDrawingBuffer: true,
        }}
      >
        <ambientLight intensity={0.5} />
        <ParticleSphere
          rotationDirection={rotationDirection}
          isPaused={isPaused}
          particleCount={particleCount}
          rotationSpeed={rotationSpeed}
          waveAmplitude={waveAmplitude}
          particleSize={particleSize}
          clustering={clustering}
          particleColor={particleColor}
        />
        <OrbitControls enableZoom={true} enablePan={true} />
      </Canvas>
    </div>
  );
}

export default App;
